/**
 * Toast History Panel
 *
 * Displays a scrollable list of all past toast notifications.
 * Shows as a slide-in panel from the right side.
 */

import React from 'react';
import { useToastStore, ToastHistoryItem, ToastType } from '../store/toastStore';
import { X, Trash2, Bell, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import './ToastHistoryPanel.css';

const getIcon = (type: ToastType) => {
  switch (type) {
    case 'success':
      return <CheckCircle size={14} className="toast-history-icon success" />;
    case 'error':
      return <AlertCircle size={14} className="toast-history-icon error" />;
    case 'warning':
      return <AlertTriangle size={14} className="toast-history-icon warning" />;
    case 'info':
    default:
      return <Info size={14} className="toast-history-icon info" />;
  }
};

const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

export const ToastHistoryPanel: React.FC = () => {
  const { history, historyVisible, setHistoryVisible, clearHistory } = useToastStore();

  if (!historyVisible) return null;

  // Show newest first
  const sortedHistory = [...history].reverse();

  return (
    <div className="toast-history-overlay" onClick={() => setHistoryVisible(false)}>
      <div className="toast-history-panel" onClick={(e) => e.stopPropagation()}>
        <div className="toast-history-header">
          <div className="toast-history-title">
            <Bell size={16} />
            <span>Notifications</span>
            <span className="toast-history-count">{history.length}</span>
          </div>
          <div className="toast-history-actions">
            {history.length > 0 && (
              <button
                className="toast-history-clear"
                onClick={clearHistory}
                title="Clear all"
              >
                <Trash2 size={14} />
              </button>
            )}
            <button
              className="toast-history-close"
              onClick={() => setHistoryVisible(false)}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="toast-history-list">
          {sortedHistory.length === 0 ? (
            <div className="toast-history-empty">
              <Bell size={32} className="toast-history-empty-icon" />
              <p>No notifications yet</p>
            </div>
          ) : (
            sortedHistory.map((item) => (
              <div key={item.id} className={`toast-history-item ${item.type}`}>
                <div className="toast-history-item-icon">
                  {getIcon(item.type)}
                </div>
                <div className="toast-history-item-content">
                  <span className="toast-history-item-message">{item.message}</span>
                  <span className="toast-history-item-time">{formatTime(item.timestamp)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ToastHistoryPanel;
